import { api } from '@api'
import { useLanguage } from '@context'
import { useQuery } from '@tanstack/react-query'
import { Profile } from './useGetProfile'

interface GetProfileByIdResponse {
  profile: Profile
}

const fetchProfileById = async (id: string, lang: string): Promise<Profile> => {
  const { data } = await api.get<GetProfileByIdResponse>(`/profile/${id}`, {
    params: { lang },
  })
  return data.profile
}

// 404 (nobody has this id, or the account was deleted) and 400 (the id is not
// a valid one) are final answers: asking again cannot change them.
export const isProfileMissingError = (error: unknown) => {
  const status = (error as { response?: { status?: number } } | undefined)?.response?.status
  return status === 404 || status === 400
}

export const useGetProfileById = (id: string) => {
  const { currentLanguage } = useLanguage()
  return useQuery<Profile>({
    queryKey: ['profile', id, currentLanguage],
    queryFn: () => fetchProfileById(id, currentLanguage),
    enabled: !!id,
    retry: (failureCount, error) => !isProfileMissingError(error) && failureCount < 3,
  })
}

